/* ============================================================
   ANIMA MOTION LAYER v2.0 — behaviour
   No dependencies. Observers and scroll listeners are passive.

   Safety contract:
   - Every feature has its own guard and its own try/catch. A
     failure in one leaves the others working and the page usable.
   - Nothing here hides content permanently. The only element ever
     set to opacity 0 is the add-to-cart LABEL, for 180ms, and it
     is restored on a timer.
   - Nothing above the fold runs until after window.load, so LCP
     cannot regress.
   - Everything is skipped under prefers-reduced-motion.
   ============================================================ */
(function () {
  'use strict';

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  if (!('IntersectionObserver' in window)) return;

  var fine = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  var booted = {};

  function noop() {}

  /* =========================================================
     1 · CARD ACTIVATION on touch / coarse pointers
     Seed marks the card nearest the viewport centre .in-view and
     plays its video. Desktop is pure CSS :hover.
     ========================================================= */
  function initCards() {
    if (fine) return;
    var cards = document.querySelectorAll('.card-wrapper');
    if (!cards.length) return;

    var seen = [];
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        var i = seen.indexOf(e.target);
        if (e.isIntersecting && i === -1) seen.push(e.target);
        if (!e.isIntersecting && i > -1) {
          seen.splice(i, 1);
          e.target.classList.remove('an-active');
        }
      });
      pick();
    }, { threshold: [0, 0.5, 1], rootMargin: '-25% 0px -25% 0px' });

    function pick() {
      var mid = window.innerHeight / 2, best = null, bestD = Infinity;
      seen.forEach(function (el) {
        var r = el.getBoundingClientRect();
        var d = Math.abs(r.top + r.height / 2 - mid);
        if (d < bestD) { bestD = d; best = el; }
      });
      Array.prototype.forEach.call(cards, function (el) {
        el.classList.toggle('an-active', el === best);
        var v = el.querySelector('video');
        if (v) { if (el === best) { var p = v.play(); if (p) p.catch(noop); } else v.pause(); }
      });
    }

    Array.prototype.forEach.call(cards, function (c) { io.observe(c); });
    window.addEventListener('scroll', pick, { passive: true });
  }

  /* =========================================================
     2 · ADD TO CART — loading and success states

     Dawn's <product-form> toggles `.hidden` on .loading__spinner
     around its fetch. Watching that class is more reliable than
     racing the fetch or reaching into Dawn's internals, and it
     keeps working if Dawn changes how it submits.

     spinner appears -> .an-loading (label dims)
     spinner hides   -> .an-added   (label swaps for 1.6s)
     ========================================================= */
  function initAddToCart() {
    if (booted.atc) return;
    var btns = document.querySelectorAll('.product-form__submit');
    if (!btns.length) return;
    booted.atc = true;

    Array.prototype.forEach.call(btns, function (btn) {
      var spinner = btn.querySelector('.loading__spinner');
      var label = btn.querySelector('span');
      if (!spinner || !label) return;

      var original = label.textContent;
      var wasLoading = false;
      var resetTimer = null;

      new MutationObserver(function () {
        var loading = !spinner.classList.contains('hidden');

        if (loading && !wasLoading) {
          wasLoading = true;
          btn.classList.add('an-loading');
          return;
        }

        if (!loading && wasLoading) {
          wasLoading = false;
          btn.classList.remove('an-loading');

          btn.classList.add('an-swap');
          setTimeout(function () {
            label.textContent = 'Added ✓';
            btn.classList.add('an-added');
            btn.classList.remove('an-swap');
          }, 180);

          clearTimeout(resetTimer);
          resetTimer = setTimeout(function () {
            btn.classList.add('an-swap');
            setTimeout(function () {
              label.textContent = original;
              btn.classList.remove('an-added');
              btn.classList.remove('an-swap');
            }, 180);
          }, 1600);
        }
      }).observe(spinner, { attributes: true, attributeFilter: ['class'] });
    });
  }

  /* =========================================================
     3 · NUTRITION PANEL COUNT-UP
     Targets .an-pn__r > span: "5,000 mg", "200 mesh", "100",
     "2 level", "0 mg". Splits the leading number from its suffix,
     counts the number, leaves the suffix alone.

     "Everything else: 0 mg" counts 0 to 0, which is the point of
     that row. It is left in rather than skipped so the whole panel
     settles at the same moment.
     ========================================================= */
  function initCountUp() {
    if (booted.count) return;
    var spans = document.querySelectorAll('.an-pn__r > span');
    if (!spans.length) return;
    booted.count = true;

    var targets = [];
    Array.prototype.forEach.call(spans, function (el) {
      var m = (el.textContent || '').trim().match(/^([\d,]+)(.*)$/);
      if (!m) return;
      var end = parseInt(m[1].replace(/,/g, ''), 10);
      if (isNaN(end)) return;
      targets.push({ el: el, end: end, suffix: m[2], grouped: m[1].indexOf(',') > -1, done: false });
    });
    if (!targets.length) return;

    var io = new IntersectionObserver(function (entries, obs) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        obs.unobserve(e.target);
        var t = null;
        for (var i = 0; i < targets.length; i++) if (targets[i].el === e.target) t = targets[i];
        if (!t || t.done) return;
        t.done = true;
        run(t);
      });
    }, { threshold: 0.6 });

    targets.forEach(function (t) { io.observe(t.el); });

    function run(t) {
      var DURATION = 900;
      var start = null;
      function frame(ts) {
        if (start === null) start = ts;
        var p = Math.min((ts - start) / DURATION, 1);
        // easeOutExpo: quick off the mark, long settle. Reads as a
        // readout locking on rather than a counter winding up.
        var eased = p === 1 ? 1 : 1 - Math.pow(2, -10 * p);
        var val = Math.round(t.end * eased);
        t.el.textContent = (t.grouped ? val.toLocaleString('en-GB') : String(val)) + t.suffix;
        if (p < 1) requestAnimationFrame(frame);
      }
      requestAnimationFrame(frame);
    }
  }

  /* =========================================================
     4 · HERO PARALLAX
     Copy and media move at different rates, as Seed does with
     Swiper parallax.

     LCP SAFETY: the hero holds the LCP element
     (<p class="an-hero__s">, measured 7 Aug). Nothing here runs
     until window.load has fired AND the browser is idle, so it
     cannot delay or displace first paint. Media is scaled 1.06 so
     the offset never exposes an edge.
     ========================================================= */
  function initHeroParallax() {
    if (booted.hero || !fine) return;   // desktop only
    var hero = document.querySelector('.an-hero');
    if (!hero) return;
    var media = hero.querySelector('video, img');
    var content = hero.querySelector('.an-hero__c');
    if (!media && !content) return;
    booted.hero = true;

    if (media) media.style.transform = 'scale(1.06)';

    var visible = true, ticking = false;
    new IntersectionObserver(function (es) {
      es.forEach(function (e) { visible = e.isIntersecting; });
    }).observe(hero);

    function update() {
      ticking = false;
      if (!visible) return;
      var y = window.scrollY;
      if (y > window.innerHeight * 1.2) return;
      if (media) media.style.transform = 'translate3d(0,' + (y * 0.18) + 'px,0) scale(1.06)';
      if (content) content.style.transform = 'translate3d(0,' + (y * 0.42) + 'px,0)';
    }

    window.addEventListener('scroll', function () {
      if (!ticking) { ticking = true; requestAnimationFrame(update); }
    }, { passive: true });
  }

  /* =========================================================
     5 · ACCORDIONS — real height animation
     <details> cannot be transitioned natively; the browser snaps
     open. Closing is intercepted so the height can run down before
     the open attribute comes off.
     ========================================================= */
  function initAccordions() {
    var items = document.querySelectorAll('details');
    if (!items.length) return;

    Array.prototype.forEach.call(items, function (d) {
      if (d.dataset.anAcc) return;
      var summary = d.querySelector('summary');
      var content = d.querySelector('.accordion__content') ||
                    (summary ? summary.nextElementSibling : null);
      if (!summary || !content) return;
      d.dataset.anAcc = '1';

      var animating = false;

      summary.addEventListener('click', function (ev) {
        if (animating) { ev.preventDefault(); return; }
        ev.preventDefault();
        animating = true;

        var startH = content.offsetHeight;
        content.style.overflow = 'hidden';

        if (!d.open) {
          d.open = true;
          content.style.height = '0px';
          var endH = content.scrollHeight;
          requestAnimationFrame(function () {
            content.style.transition = 'height .32s cubic-bezier(.165,.84,.44,1)';
            content.style.height = endH + 'px';
          });
          after(function () {});
        } else {
          content.style.height = startH + 'px';
          requestAnimationFrame(function () {
            content.style.transition = 'height .28s cubic-bezier(.165,.84,.44,1)';
            content.style.height = '0px';
          });
          after(function () { d.open = false; });
        }

        function after(fn) {
          setTimeout(function () {
            content.style.transition = '';
            content.style.height = '';
            content.style.overflow = '';
            try { fn(); } catch (e) {}
            animating = false;
          }, 340);
        }
      });
    });
  }

  /* =========================================================
     6 · HEADER PILL MORPH
     ========================================================= */
  function initHeader() {
    if (booted.header) return;
    var h = document.querySelector('.section-header') ||
            document.querySelector('#shopify-section-header');
    if (!h) return;
    booted.header = true;

    var ticking = false;
    function update() {
      h.classList.toggle('an-scrolled', window.scrollY > 120);
      ticking = false;
    }
    window.addEventListener('scroll', function () {
      if (!ticking) { ticking = true; requestAnimationFrame(update); }
    }, { passive: true });
    update();
  }

  /* =========================================================
     7 · IN-VIEW VIDEO — play only what can be seen
     Skips the hero: its playback belongs to the LCP fix, which
     decides whether that video should load at all.
     ========================================================= */
  function initVideos() {
    var vids = document.querySelectorAll('video[loop], video[autoplay]');
    if (!vids.length) return;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        var v = e.target;
        if (e.isIntersecting) { var p = v.play(); if (p) p.catch(noop); }
        else v.pause();
      });
    }, { threshold: 0.15 });
    Array.prototype.forEach.call(vids, function (v) {
      if (v.closest && v.closest('.an-hero')) return;
      io.observe(v);
    });
  }

  /* --------------------------------------------------------
     Boot. The theme editor fires shopify:section:load on every
     edit, so card, video and accordion setup re-run against the
     new DOM; anything attaching a global listener or rewriting
     text is guarded by `booted` and runs exactly once.
     -------------------------------------------------------- */
  function boot() {
    try { initCards();      } catch (e) {}
    try { initHeader();     } catch (e) {}
    try { initVideos();     } catch (e) {}
    try { initAddToCart();  } catch (e) {}
    try { initCountUp();    } catch (e) {}
    try { initAccordions(); } catch (e) {}
  }

  function bootDeferred() {
    var go = function () { try { initHeroParallax(); } catch (e) {} };
    if ('requestIdleCallback' in window) requestIdleCallback(go, { timeout: 2000 });
    else setTimeout(go, 400);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  if (document.readyState === 'complete') bootDeferred();
  else window.addEventListener('load', bootDeferred);

  document.addEventListener('shopify:section:load', boot);
})();
